import { CurrencyPipe } from '@angular/common';
import { Component, computed, inject, OnInit, output, OutputEmitterRef, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { InfoBannerComponent, ModalComponent, SpinnerComponent, TagComponent } from '@catch/general-ui';
import { ScreenService } from '@catch/services';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LazyLoadEvent } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { debounceTime, merge } from 'rxjs';
import { JobPlatform } from '../../interfaces/job-platform';
import { FormatPricePipe } from '../../pipes/format-price.pipe';
import { FontawesomeService } from '../../services/fontawesome.service';
import { JobPlatformsService } from '../../services/job-platforms.service';
import { ButtonComponent } from '../button/button.component';
import { ViewLoaderComponent } from '../view-loader/view-loader.component';

@UntilDestroy()
@Component({
  selector: 'app-job-platforms-browser',
  templateUrl: './job-platforms-browser.component.html',
  styleUrl: './job-platforms-browser.component.scss',
  imports: [
    CurrencyPipe,
    ReactiveFormsModule,
    FaIconComponent,
    TranslocoPipe,
    TableModule,
    ModalComponent,
    SpinnerComponent,
    InfoBannerComponent,
    TagComponent,
    FormatPricePipe,
    ViewLoaderComponent,
    ButtonComponent,
  ],
})
export class JobPlatformsBrowserComponent implements OnInit {
  readonly jobPlatformsService = inject(JobPlatformsService);
  readonly translateService = inject(TranslocoService);
  readonly fontawesomeService = inject(FontawesomeService);
  readonly screenService = inject(ScreenService);

  @ViewChild('jobPlatformsTable') jobPlatformsTable: Table;
  @ViewChild('jobPlatformsModal') jobPlatformsModal: any;
  page: WritableSignal<number> = signal(1);
  rows: WritableSignal<number> = signal(10);
  offset: number = 0;

  jobPlatformFilter = new FormControl();
  jobCrossPostings: string[] = [];
  openDescriptions: WritableSignal<string[]> = signal([]);

  selectedPlatformsOutput: OutputEmitterRef<JobPlatform[]> = output();
  selectedPlatforms: WritableSignal<JobPlatform[]> = signal([]);

  ngOnInit(): void {
    this.jobPlatformsService.loadPlatforms(this.page(), this.jobPlatformFilter.value);
    merge(this.translateService.langChanges$, this.jobPlatformFilter.valueChanges.pipe(debounceTime(300)))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.page.set(1);
        this.jobPlatformsService.loadPlatforms(this.page(), this.jobPlatformFilter.value);
      });
  }

  onPaginationChange(event: LazyLoadEvent): void {
    const page = event.first !== undefined ? Math.floor(event.first / event.rows) + 1 : 1;

    this.jobPlatformsService.loadPlatforms(page, this.jobPlatformFilter.value);

    const checkLoading = setInterval(() => {
      if (!this.jobPlatformsService.platformsLoading()) {
        clearInterval(checkLoading);

        // Reset table scroll
        if (this.jobPlatformsTable) {
          this.jobPlatformsTable.resetScrollTop();
        }

        // Small delay to ensure DOM update
        setTimeout(() => {
          // Target the modal-content directly
          const modalContent = document.querySelector('.jobplatforms-modal .modal-content');
          if (modalContent) {
            modalContent.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }
        }, 50);
      }
    }, 100);

    setTimeout(() => clearInterval(checkLoading), 5000);
  }

  isDescriptionOpen(jobPlatformId: string): boolean {
    return this.openDescriptions().includes(jobPlatformId);
  }

  openDescription(jobPlatformId: string): void {
    this.openDescriptions.update((descriptions) => [...descriptions, jobPlatformId]);
  }

  closeDescription(jobPlatformId: string): void {
    this.openDescriptions.update((descriptions) => descriptions.filter((id) => id !== jobPlatformId));
  }

  openJobCrossPostingModal(modal: any, jobPlatform: JobPlatform) {
    this.jobCrossPostings = jobPlatform.crossPostings ?? [];
    modal.open();
  }
  displayedJobPlatforms = computed((): JobPlatform[] => {
    const uniquePlatforms = new Map<string, JobPlatform>();

    this.jobPlatformsService.recommendedPlatforms().forEach((p) => uniquePlatforms.set(`${p.provider}-${p.id}`, p));
    this.selectedPlatforms().forEach((p) => uniquePlatforms.set(`${p.provider}-${p.id}`, p));

    return [...Array.from(uniquePlatforms.values())];
  });

  isPlatformSelected(jobPlatform: JobPlatform): boolean {
    return this.selectedPlatforms().some(
      (p) => String(p.id) === String(jobPlatform.id) && p.provider === jobPlatform.provider
    );
  }

  onPlatformSelectionChange(jobPlatform: JobPlatform): void {
    const index = this.selectedPlatforms().findIndex(
      (p) => String(p.id) === String(jobPlatform.id) && p.provider === jobPlatform.provider
    );
    if (index === -1) {
      this.selectedPlatforms.set([
        ...this.selectedPlatforms(),
        { ...jobPlatform, selected: true },
      ]);
    } else {
      this.selectedPlatforms.set(
        this.selectedPlatforms().filter(
          (p) => String(p.id) !== String(jobPlatform.id) || p.provider !== jobPlatform.provider
        )
      );
    }

    this.selectedPlatformsOutput.emit(this.selectedPlatforms());
  }

  trackPlatformsBy(index: number, element: JobPlatform) {
    return `${element.provider}-${element.id}`;
  }

  isNaN(value: number): boolean {
    return Number.isNaN(value);
  }

  ceiling(value: number): number {
    return Math.ceil(value);
  }
}
